/**
 * Calculation engine for TanTable's totals bar (the toggleable aggregation
 * footer). Pure functions only — the component layer pulls raw values off the
 * filtered rows, calls in here, and renders whatever comes back.
 *
 * Design: docs/plans/2026-08-27-tantable-aggregation-footer-design.md
 */

import { formatCurrency } from './money';

/** A calculation the user can pin to a column. */
export type AggId =
  | 'sum'
  | 'avg'
  | 'median'
  | 'min'
  | 'max'
  | 'count'
  | 'countDistinct'
  | 'earliest'
  | 'latest';

/**
 * What kind of data a column holds, which decides the calcs on offer.
 * 'money' behaves as 'number' for calculation and adds currency formatting.
 * 'none' suppresses the column's slot entirely.
 */
export type AggColumnType = 'number' | 'money' | 'date' | 'text' | 'none';

/**
 * What gets persisted per column. 'none' is an explicit clear, stored rather
 * than deleted so a type default can't creep back over the user's choice.
 */
export type AggCalcSetting = AggId | 'none';

export interface AggResult {
  /** 'empty' means "nothing to calculate" — renders as an em dash, never as 0. */
  kind: 'number' | 'count' | 'date' | 'empty';
  value: number | string | Date | null;
}

export const AGG_LABELS: Record<AggId, string> = {
  sum: 'Sum',
  avg: 'Average',
  median: 'Median',
  min: 'Min',
  max: 'Max',
  count: 'Count',
  countDistinct: 'Count distinct',
  earliest: 'Earliest',
  latest: 'Latest',
};

const NUMERIC_CALCS: AggId[] = ['sum', 'avg', 'median', 'min', 'max', 'count', 'countDistinct'];
const DATE_CALCS: AggId[] = ['earliest', 'latest', 'count', 'countDistinct'];
const TEXT_CALCS: AggId[] = ['count', 'countDistinct'];

/** How many non-blank values to sample when inferring a column's type. */
const TYPE_SAMPLE_SIZE = 50;

const ZERO_DATES = new Set(['0000-00-00', '0000-00-00 00:00:00']);

/**
 * A value that contributes nothing to a calculation: null/undefined, an empty or
 * whitespace-only string, or a MySQL zero-date. A real 0 or false is NOT blank.
 */
export function isBlankAggValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed === '' || ZERO_DATES.has(trimmed);
  }
  return false;
}

/**
 * Coerce a cell value to a finite number, tolerating the currency/percent
 * formatting that some API fields arrive with ("$1,234.50"). Returns null for
 * anything that isn't genuinely numeric — including the values `Number()` would
 * happily turn into 0 or 1 (`[]`, `true`), which would silently poison a Sum.
 */
export function coerceNumeric(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string') return null;
  const cleaned = value.replace(/[$,%\s]/g, '');
  if (cleaned === '' || cleaned === '-') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** Local "YYYY-MM-DDTHH:MM:SS" — the shape TanTable's own formatDate can parse. */
function toParseableDateString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

/** Parse a date-ish cell value to a timestamp, mirroring TanTable's own date parsing. */
function parseDateValue(value: unknown): number | null {
  if (isBlankAggValue(value)) return null;
  if (value instanceof Date) {
    const ts = value.getTime();
    return Number.isNaN(ts) ? null : ts;
  }
  if (typeof value !== 'string') return null;
  const raw = value.trim();
  let dateObj: Date;
  if (raw.includes('T')) {
    dateObj = new Date(raw);
  } else if (raw.includes(' ')) {
    const [datePart, timePart] = raw.split(' ');
    dateObj = new Date(`${datePart}T${timePart}`);
  } else {
    dateObj = new Date(`${raw}T00:00:00`);
  }
  const ts = dateObj.getTime();
  return Number.isNaN(ts) ? null : ts;
}

/** Pull display text out of a rendered cell value (a string, or a React element). */
function extractRenderedText(rendered: unknown, depth = 0): string {
  if (depth > 6 || rendered === null || rendered === undefined) return '';
  if (typeof rendered === 'string') return rendered;
  if (typeof rendered === 'number' || typeof rendered === 'boolean') return String(rendered);
  if (Array.isArray(rendered)) {
    return rendered.map((child) => extractRenderedText(child, depth + 1)).join('');
  }
  if (typeof rendered === 'object' && 'props' in (rendered as any)) {
    return extractRenderedText((rendered as any).props?.children, depth + 1);
  }
  return '';
}

/**
 * Whether a column's rendered cell reads as currency, which is how a numeric
 * column earns currency formatting on its totals. Money cells in this codebase
 * render as a "$..." string (or a span wrapping one).
 */
export function renderedTextLooksLikeMoney(rendered: unknown): boolean {
  if (typeof rendered === 'number' || typeof rendered === 'boolean') return false;
  const text = extractRenderedText(rendered).trim();
  return /^-?\$/.test(text);
}

/**
 * Decide what kind of data a column holds.
 *
 * Precedence: an explicit `meta.aggType` wins, then the existing `meta.isDate`
 * flag, then a sample of the actual values. `rendersAsMoney` (the component
 * checks whether the rendered cell text starts with "$") upgrades a numeric
 * column to money.
 */
export function resolveAggColumnType(opts: {
  values: unknown[];
  meta?: any;
  rendersAsMoney?: boolean;
}): AggColumnType {
  const { values, meta, rendersAsMoney } = opts;

  const declared = meta?.aggType as AggColumnType | undefined;
  if (declared === 'money' || declared === 'number' || declared === 'date' ||
      declared === 'text' || declared === 'none') {
    return declared;
  }

  if (meta?.isDate) return 'date';

  const sample: unknown[] = [];
  for (const value of values) {
    if (isBlankAggValue(value)) continue;
    sample.push(value);
    if (sample.length >= TYPE_SAMPLE_SIZE) break;
  }

  // Nothing to go on (e.g. filtered to zero rows) — offer counts only.
  if (sample.length === 0) return 'text';

  const allNumeric = sample.every((value) => coerceNumeric(value) !== null);
  if (!allNumeric) return 'text';
  return rendersAsMoney ? 'money' : 'number';
}

/** The calcs offered for a column type, in menu order. */
export function calcsForType(type: AggColumnType): AggId[] {
  switch (type) {
    case 'number':
    case 'money':
      return [...NUMERIC_CALCS];
    case 'date':
      return [...DATE_CALCS];
    case 'text':
      return [...TEXT_CALCS];
    default:
      return [];
  }
}

const ALL_AGG_IDS = Object.keys(AGG_LABELS) as AggId[];

/**
 * Read a field definition's `aggregate_default` (Field Manager > Edit Field
 * Definition). The column is a plain VARCHAR with no DB-level enum, so anything
 * unrecognized degrades to "no default" rather than being trusted.
 */
export function normalizeAggDefault(value: unknown): AggId | null {
  if (typeof value !== 'string') return null;
  const wanted = value.trim().toLowerCase();
  if (wanted === '' || wanted === 'none') return null;
  return ALL_AGG_IDS.find((id) => id.toLowerCase() === wanted) ?? null;
}

/**
 * The calc a column actually shows, highest layer wins:
 *   1. `user`  — this user's own pick for this grid, or their explicit clear ('none'),
 *   2. `admin` — the published default for THIS grid ("Save as Defaults"),
 *   3. `field` — the field definition's aggregate_default (Field Manager),
 *   4. nothing — there is no built-in "numbers always sum" fallback, so a column
 *      totals only where somebody asked for it.
 *
 * The grid-specific admin default outranks the field-wide one because it is the
 * more specific statement: the field definition applies to that field wherever
 * it appears, the grid default only here.
 *
 * A default applies even when it doesn't match the inferred type; the calc
 * itself reports an em dash if the values can't support it.
 */
export function resolveEffectiveCalc(
  type: AggColumnType,
  sources: { user?: AggCalcSetting; admin?: unknown; field?: unknown },
): AggId | null {
  if (type === 'none') return null;
  const { user, admin, field } = sources;
  if (user === 'none') return null;
  if (user) return user;
  return normalizeAggDefault(admin) ?? normalizeAggDefault(field);
}

/**
 * Whether the totals bar shows. `userSetting` is tri-state on purpose, following
 * the same convention as `superCompactOverride` / `localFilterOverride`: null
 * means the user has never touched the toggle on this grid, so an admin default
 * still reaches them. Once they toggle it, their choice sticks either way.
 */
export function resolveShowAggFooter(
  userSetting: boolean | null | undefined,
  adminDefault: boolean | null | undefined,
): boolean {
  if (typeof userSetting === 'boolean') return userSetting;
  return adminDefault === true;
}

const EMPTY_RESULT: AggResult = { kind: 'empty', value: null };

/** Normalize a value for distinct-counting: numbers by value, strings case-insensitively. */
function distinctKey(value: unknown): string {
  const n = coerceNumeric(value);
  if (n !== null) return `num:${n}`;
  return `str:${String(value).trim().toLowerCase()}`;
}

/**
 * Run one calculation over a column's raw values.
 *
 * Blank and non-coercible values are excluded rather than treated as 0. When
 * nothing survives, the result is `empty` (rendered as an em dash) — a bad feed
 * must never masquerade as a real $0.00 total. Counts are the exception: zero is
 * a meaningful answer there.
 */
export function computeAggregation(calc: AggId, values: unknown[]): AggResult {
  if (calc === 'count') {
    let count = 0;
    for (const value of values) if (!isBlankAggValue(value)) count++;
    return { kind: 'count', value: count };
  }

  if (calc === 'countDistinct') {
    const seen = new Set<string>();
    for (const value of values) {
      if (isBlankAggValue(value)) continue;
      seen.add(distinctKey(value));
    }
    return { kind: 'count', value: seen.size };
  }

  if (calc === 'earliest' || calc === 'latest') {
    let bestTs: number | null = null;
    let bestRaw: string | Date | null = null;
    for (const value of values) {
      const ts = parseDateValue(value);
      if (ts === null) continue;
      const better =
        bestTs === null || (calc === 'earliest' ? ts < bestTs : ts > bestTs);
      if (better) {
        bestTs = ts;
        bestRaw = value as string | Date;
      }
    }
    return bestRaw === null ? EMPTY_RESULT : { kind: 'date', value: bestRaw };
  }

  const numbers: number[] = [];
  for (const value of values) {
    const n = coerceNumeric(value);
    if (n !== null) numbers.push(n);
  }
  if (numbers.length === 0) return EMPTY_RESULT;

  switch (calc) {
    case 'sum':
      return { kind: 'number', value: numbers.reduce((a, b) => a + b, 0) };
    case 'avg':
      return { kind: 'number', value: numbers.reduce((a, b) => a + b, 0) / numbers.length };
    case 'min':
      return { kind: 'number', value: Math.min(...numbers) };
    case 'max':
      return { kind: 'number', value: Math.max(...numbers) };
    case 'median': {
      const sorted = [...numbers].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      const value =
        sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
      return { kind: 'number', value };
    }
    default:
      return EMPTY_RESULT;
  }
}

/**
 * Render a result for display. Money uses the shared `formatCurrency`; plain
 * numbers show decimals only when they have them; dates go through the caller's
 * formatter (TanTable passes its own `formatDate`).
 */
export function formatAggValue(
  result: AggResult,
  opts?: { money?: boolean; formatDate?: (value: string) => string },
): string {
  if (result.kind === 'empty' || result.value === null) return '—';

  if (result.kind === 'date') {
    const raw =
      result.value instanceof Date ? toParseableDateString(result.value) : String(result.value);
    return opts?.formatDate ? opts.formatDate(raw) : raw;
  }

  const n = Number(result.value);
  if (!Number.isFinite(n)) return '—';

  if (result.kind === 'count') return Math.round(n).toLocaleString();

  if (opts?.money) return formatCurrency(n);

  return Number.isInteger(n)
    ? n.toLocaleString()
    : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
